import { useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { ProgressOverlayStatus, useProgressOverlay } from '@fs/zion-ui'
import { useQueryParams } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { adjustQueryParamsForSearch, validateSearchCriteria } from 'helpers'
import { downloadSearchResults } from 'queries/groupSearchQueries'
import { downloadJiapuSearchResults, downloadJokboSearchResults } from 'queries/volumeSetQueries'
import useLanguageFormat from '../useLanguageFormat'
import useImageDetailsUrl from '../useImageDetailsUrl'
import AllRecordTypesCsv from './AllRecordTypesCsv'
import JiapuCsv from './JiapuCsv'
import JokboCsv from './JokboCsv'

const DOWNLOAD_LIMIT = 50000
const RESULTS_PER_PAGE = 100

export default function useDownloadGroupSearch() {
  const downloadLimit = useFeatureFlag('lightyear_lightyearReact_downloadLimit')
  const addActiveColumnFlag = useFeatureFlag('lightyear_lightyearReact_includeDeprecatedImageGroups')
  const downloadStreamFlag = useFeatureFlag('lightyear_lightyearReact_downloadStream')
  const UPDATED_RESULTS_PER_PAGE = downloadLimit.isOn ? 200 : RESULTS_PER_PAGE
  const { IN_PROGRESS, SUCCESSFUL, FAILED } = ProgressOverlayStatus
  const { createProgressItem } = useProgressOverlay()
  const { query } = useQueryParams()
  const [inProgress, setInProgress] = useState(false)
  const getImageDetailsUrl = useImageDetailsUrl()
  const [t] = useTranslation()
  const { getLanguageFromCode } = useLanguageFormat()

  const isVolumeSetSearch = query.jiapuOnly || Boolean(query.volumeSetSearch)
  let downloadQuery = downloadSearchResults

  let csvInstance
  if (isVolumeSetSearch) {
    if (query.volumeSetSearch === 'jokbo') {
      downloadQuery = downloadJokboSearchResults
      csvInstance = new JokboCsv()
    } else {
      downloadQuery = downloadJiapuSearchResults
      csvInstance = new JiapuCsv()
    }
  } else {
    csvInstance = new AllRecordTypesCsv({ doDownloadStream: downloadStreamFlag.isOn })
  }

  const [performSearch] = useLazyQuery(downloadQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  })

  let startNextBatch = true
  let hasRework = false

  const fetchData = () => {
    return new Promise((resolve) => {
      const errors = validateSearchCriteria(query)

      if (errors) {
        return
      }

      if (query.hasRework) {
        hasRework = true
      }
      // Do the actual back-end query
      resolve(
        performSearch({
          variables: {
            multipleClanBranches: query.volumeSetSearch === 'jokbo',
            searchInput: adjustQueryParamsForSearch(query),
            withRework: hasRework,
          },
          context: {
            headers: {
              fs_operations_call: 'fs-lightyear',
            },
          },
        })
      )
    })
  }

  const downloadCsvFile = async () => {
    setInProgress(true)

    const update = createProgressItem(
      {
        message: t('groupSearchDownload.inProgress', 'Downloading spreadsheet of results.'),
        status: IN_PROGRESS,
        percent: 0,
      },
      () => {
        startNextBatch = false
        setInProgress(false)
      }
    )

    const handleDownloadError = (errorMessage) => {
      console.error(`Error downloading csv: ${errorMessage}`)
      update({
        message: t(
          'groupSearchDownload.failed',
          'Unable to download all Search Results. Try again later for all results.'
        ),
        status: FAILED,
      })
      csvInstance.download()
      setInProgress(false)
    }

    const handleCancelDownload = (errorMessage) => {
      console.error(`Error writing to csv, cancelling download: ${errorMessage}`)
      update({
        message: t('groupSearchDownload.cancelled', 'Download Cancelled.'),
        status: FAILED,
      })
      setInProgress(false)
    }

    let finishedGroups = 0
    let nextPageToken = ''
    let totalCount

    update({ percent: 0 })

    do {
      if (!startNextBatch) {
        return
      }

      query.pageSize = UPDATED_RESULTS_PER_PAGE
      query.nextPageToken = nextPageToken

      let res
      try {
        // eslint-disable-next-line no-await-in-loop
        res = await fetchData()
      } catch (e) {
        handleDownloadError(e)
        return
      }
      const data = res?.data

      if (!data || res.errors) {
        handleDownloadError(res.errors.map((error) => error.message).join(', '))
        return
      }

      const searchResults = query.volumeSetSearch ? data?.volumeSetSearch : data?.search

      // NOTE: getImageDetailsUrl is only used by AllRecordTypesCsv
      try {
        // eslint-disable-next-line no-await-in-loop
        await csvInstance.generateRowsWithStream(
          searchResults?.groups,
          getImageDetailsUrl,
          getLanguageFromCode,
          addActiveColumnFlag
        )
      } catch (e) {
        // usually in this state when user doesn't choose place to save the file when using download stream
        handleCancelDownload(e)
        return
      }

      totalCount = downloadLimit.isOn ? searchResults.totalCount : Math.min(searchResults.totalCount, DOWNLOAD_LIMIT)
      finishedGroups += UPDATED_RESULTS_PER_PAGE
      const percentComplete = Math.floor((finishedGroups / totalCount) * 100)
      update({ percent: percentComplete })
      nextPageToken = searchResults.nextPageToken
    } while (totalCount > finishedGroups)

    update({ percent: 100 })

    csvInstance.download()

    update({ message: t('groupSearchDownload.successful', 'Successfully downloaded spreadsheet.'), status: SUCCESSFUL })
    setInProgress(false)
  }

  return {
    downloadCsvFile,
    inProgress,
  }
}
